/**
 * Trip Context Service
 * 
 * This service extracts contextual information about trips to provide
 * intelligent context for AI-powered resource generation. It analyzes
 * Days, Accommodation, and Travel data to determine trip dates, duration,
 * and context for specific places.
 * 
 * Features:
 * - Date range extraction from multiple data sources
 * - Trip duration calculation
 * - Place-specific context determination
 * - Contextual formatting for AI prompts
 * - Fallback handling for incomplete data
 */

import { 
  getDays, 
  getAccommodation, 
  getTravel 
} from '@trip-planner-monorepo/shared';

/**
 * Extracts date range from Days data
 * @param {Array} days - Array of day objects
 * @returns {Object|null} Date range object or null if no valid dates
 */
function extractDateRangeFromDays(days) {
  if (!days || !Array.isArray(days) || days.length === 0) {
    return null;
  }

  // Filter days that have actual dates
  const daysWithDates = days.filter(day => day.date && day.date.trim());
  
  if (daysWithDates.length === 0) {
    return null;
  }

  // Sort dates and get range
  const dates = daysWithDates.map(day => day.date).sort();
  
  return {
    startDate: dates[0],
    endDate: dates[dates.length - 1],
    totalDays: dates.length,
    source: 'days'
  };
}

/**
 * Extracts date range from Accommodation data
 * @param {Array} accommodations - Array of accommodation objects
 * @returns {Object|null} Date range object or null if no valid dates
 */
function extractDateRangeFromAccommodations(accommodations) {
  if (!accommodations || !Array.isArray(accommodations) || accommodations.length === 0) {
    return null;
  }

  const validAccommodations = accommodations.filter(acc => 
    acc.details && acc.details.checkInDate
  );

  if (validAccommodations.length === 0) {
    return null;
  }

  // Get all check-in and check-out dates
  const checkInDates = validAccommodations
    .map(acc => acc.details.checkInDate)
    .filter(date => date);
  
  const checkOutDates = validAccommodations
    .map(acc => acc.details.checkOutDate)
    .filter(date => date);

  // Combine all dates
  const allDates = [...checkInDates, ...checkOutDates].sort();
  
  if (allDates.length === 0) {
    return null;
  }

  return {
    startDate: allDates[0],
    endDate: allDates[allDates.length - 1],
    totalAccommodations: validAccommodations.length,
    source: 'accommodations'
  };
}

/**
 * Extracts date range from Travel data
 * @param {Array} travelItems - Array of travel objects
 * @returns {Object|null} Date range object or null if no valid dates
 */
function extractDateRangeFromTravel(travelItems) {
  if (!travelItems || !Array.isArray(travelItems) || travelItems.length === 0) {
    return null;
  }

  const validTravelItems = travelItems.filter(travel => 
    travel.details && (travel.details.departureDate || travel.details.arrivalDate)
  );

  if (validTravelItems.length === 0) {
    return null;
  }

  // Get all departure and arrival dates
  const departureDates = validTravelItems
    .map(travel => travel.details.departureDate)
    .filter(date => date);
  
  const arrivalDates = validTravelItems
    .map(travel => travel.details.arrivalDate)
    .filter(date => date);

  // Combine all dates
  const allDates = [...departureDates, ...arrivalDates].sort();
  
  if (allDates.length === 0) {
    return null;
  }

  return {
    startDate: allDates[0],
    endDate: allDates[allDates.length - 1],
    totalTravelItems: validTravelItems.length,
    source: 'travel'
  };
}

/**
 * Determines the best date range from multiple sources
 * @param {Object} daysRange - Date range from days data
 * @param {Object} accommodationsRange - Date range from accommodations data
 * @param {Object} travelRange - Date range from travel data
 * @returns {Object|null} Best date range or null if no valid data
 */
function determineBestDateRange(daysRange, accommodationsRange, travelRange) {
  const ranges = [daysRange, accommodationsRange, travelRange].filter(Boolean);
  
  if (ranges.length === 0) {
    return null;
  }

  // Priority: Days > Accommodations > Travel
  // Days are most reliable as they represent actual planned activities
  if (daysRange) {
    return {
      ...daysRange,
      confidence: 'high',
      sources: ['days']
    };
  }

  // If no days, use accommodations
  if (accommodationsRange) {
    return {
      ...accommodationsRange,
      confidence: 'medium',
      sources: ['accommodations']
    };
  }

  // Fallback to travel
  if (travelRange) {
    return {
      ...travelRange,
      confidence: 'low',
      sources: ['travel']
    };
  }

  return null;
}

/**
 * Calculates trip duration in days
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 * @returns {number} Duration in days
 */
function calculateTripDuration(startDate, endDate) {
  if (!startDate || !endDate) {
    return 0;
  }

  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 0;
  }

  // Calculate difference in days
  const timeDiff = end.getTime() - start.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days
  
  return Math.max(0, daysDiff);
}

/**
 * Determines if a place is related to specific accommodations or travel
 * @param {string} placeId - The place ID to check
 * @param {Array} accommodations - Array of accommodation objects
 * @param {Array} travelItems - Array of travel objects
 * @returns {Object} Context information about the place
 */
function determinePlaceContext(placeId, accommodations, travelItems) {
  const context = {
    isAccommodationRelated: false,
    isTravelRelated: false,
    relatedAccommodations: [],
    relatedTravelItems: [],
    contextType: 'general'
  };

  // Check accommodation relationships
  if (accommodations && Array.isArray(accommodations)) {
    const relatedAccommodations = accommodations.filter(acc => {
      // Handle both old simple structure and new nested structure
      if (typeof acc.placeReference === 'string') {
        return acc.placeReference === placeId;
      } else if (acc.placeReference && typeof acc.placeReference === 'object') {
        return acc.placeReference.placeId === placeId;
      }
      return false;
    });

    if (relatedAccommodations.length > 0) {
      context.isAccommodationRelated = true;
      context.relatedAccommodations = relatedAccommodations;
      context.contextType = 'accommodation';
    }
  }

  // Check travel relationships
  if (travelItems && Array.isArray(travelItems)) {
    const relatedTravelItems = travelItems.filter(travel => 
      travel.departurePlace === placeId || travel.arrivalPlace === placeId
    );

    if (relatedTravelItems.length > 0) {
      context.isTravelRelated = true;
      context.relatedTravelItems = relatedTravelItems;
      
      // If not already accommodation-related, set as travel-related
      if (!context.isAccommodationRelated) {
        context.contextType = 'travel';
      } else {
        context.contextType = 'mixed';
      }
    }
  }

  return context;
}

/**
 * Formats date range for AI prompts
 * @param {Object} dateRange - Date range object
 * @returns {string} Formatted date range string
 */
function formatDateRangeForPrompt(dateRange) {
  if (!dateRange || !dateRange.startDate) {
    return 'an unspecified date';
  }

  const startDate = new Date(dateRange.startDate);
  const endDate = dateRange.endDate ? new Date(dateRange.endDate) : null;

  // Format start date
  const startFormatted = startDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // If no end date or same as start date
  if (!endDate || startDate.getTime() === endDate.getTime()) {
    return startFormatted;
  }

  // Format end date
  const endFormatted = endDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // If same year, don't repeat it
  if (startDate.getFullYear() === endDate.getFullYear()) {
    const endFormattedShort = endDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
    return `${startFormatted} to ${endFormattedShort}`;
  }

  return `${startFormatted} to ${endFormatted}`;
}

/**
 * Main function to extract trip context for a place
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID (optional, for place-specific context)
 * @returns {Promise<Object>} Trip context object
 */
export async function extractTripContext(userId, tripId, placeId = null) {
  try {
    console.log(`🔍 [TripContext] Extracting trip context for user ${userId}, trip ${tripId}, place ${placeId || 'all'}`);

    // Fetch all trip data in parallel
    const [days, accommodations, travelItems] = await Promise.all([
      getDays(userId, tripId).catch(error => {
        console.warn('⚠️ [TripContext] Error fetching days:', error.message);
        return [];
      }),
      getAccommodation(userId, tripId).catch(error => {
        console.warn('⚠️ [TripContext] Error fetching accommodations:', error.message);
        return [];
      }),
      getTravel(userId, tripId).catch(error => {
        console.warn('⚠️ [TripContext] Error fetching travel items:', error.message);
        return [];
      })
    ]);

    // Extract date ranges from each source
    const daysRange = extractDateRangeFromDays(days);
    const accommodationsRange = extractDateRangeFromAccommodations(accommodations);
    const travelRange = extractDateRangeFromTravel(travelItems);

    // Determine the best date range
    const bestDateRange = determineBestDateRange(daysRange, accommodationsRange, travelRange);

    // Calculate trip duration
    const duration = bestDateRange ? 
      calculateTripDuration(bestDateRange.startDate, bestDateRange.endDate) : 0;

    // Determine place-specific context if placeId provided
    let placeContext = null;
    if (placeId) {
      placeContext = determinePlaceContext(placeId, accommodations, travelItems);
    }

    // Format date range for AI prompts
    const formattedDateRange = bestDateRange ? 
      formatDateRangeForPrompt(bestDateRange) : 'an unspecified date';

    const context = {
      // Date information
      dateRange: bestDateRange,
      formattedDateRange,
      duration,
      
      // Data sources
      daysCount: days ? days.length : 0,
      accommodationsCount: accommodations ? accommodations.length : 0,
      travelItemsCount: travelItems ? travelItems.length : 0,
      
      // Place-specific context
      placeContext,
      
      // Metadata
      extractedAt: new Date().toISOString(),
      confidence: bestDateRange ? bestDateRange.confidence : 'none'
    };

    console.log(`✅ [TripContext] Trip context extracted successfully:`, {
      hasDateRange: !!bestDateRange,
      duration,
      formattedDateRange,
      placeContextType: placeContext?.contextType || 'none'
    });

    return context;

  } catch (error) {
    console.error('❌ [TripContext] Error extracting trip context:', error);
    
    // Return fallback context
    return {
      dateRange: null,
      formattedDateRange: 'an unspecified date',
      duration: 0,
      daysCount: 0,
      accommodationsCount: 0,
      travelItemsCount: 0,
      placeContext: null,
      extractedAt: new Date().toISOString(),
      confidence: 'none',
      error: error.message
    };
  }
}

/**
 * Extracts context for multiple places in a trip
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string[]} placeIds - Array of place IDs
 * @returns {Promise<Object>} Context object with place-specific information
 */
export async function extractMultiPlaceContext(userId, tripId, placeIds) {
  try {
    console.log(`🔍 [TripContext] Extracting multi-place context for ${placeIds.length} places`);

    // Get base trip context
    const baseContext = await extractTripContext(userId, tripId);

    // Extract context for each place
    const placeContexts = {};
    
    for (const placeId of placeIds) {
      const placeContext = await extractTripContext(userId, tripId, placeId);
      placeContexts[placeId] = placeContext.placeContext;
    }

    return {
      ...baseContext,
      placeContexts
    };

  } catch (error) {
    console.error('❌ [TripContext] Error extracting multi-place context:', error);
    throw error;
  }
}

/**
 * Gets trip context summary for display purposes
 * @param {Object} context - Trip context object
 * @returns {string} Human-readable context summary
 */
export function getTripContextSummary(context) {
  if (!context) {
    return 'No trip context available';
  }

  const parts = [];

  // Add date information
  if (context.formattedDateRange && context.formattedDateRange !== 'an unspecified date') {
    parts.push(`Trip dates: ${context.formattedDateRange}`);
  }

  // Add duration
  if (context.duration > 0) {
    parts.push(`Duration: ${context.duration} day${context.duration !== 1 ? 's' : ''}`);
  }

  // Add data counts
  const counts = [];
  if (context.daysCount > 0) counts.push(`${context.daysCount} day${context.daysCount !== 1 ? 's' : ''}`);
  if (context.accommodationsCount > 0) counts.push(`${context.accommodationsCount} accommodation${context.accommodationsCount !== 1 ? 's' : ''}`);
  if (context.travelItemsCount > 0) counts.push(`${context.travelItemsCount} travel item${context.travelItemsCount !== 1 ? 's' : ''}`);

  if (counts.length > 0) {
    parts.push(`Includes: ${counts.join(', ')}`);
  }

  // Add confidence level
  if (context.confidence && context.confidence !== 'none') {
    parts.push(`Confidence: ${context.confidence}`);
  }

  return parts.length > 0 ? parts.join(' | ') : 'Basic trip information available';
}

// Export the service object
export const tripContextService = {
  extractTripContext,
  extractMultiPlaceContext,
  getTripContextSummary,
  formatDateRangeForPrompt,
  calculateTripDuration
};

export default tripContextService;
